import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Paper } from '@mui/material';
import chartSx from './Chart.styles';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import ChartingMenu from './ChartingMenu';
import CanvasBuccal from './CanvasBuccal';
import CanvasOcclusal from './CanvasOcclusal';
import CanvasPerio from './CanvasPerio';
import PerioNumpad from './CanvasPerio/PerioNumpad';
import PerioToothNumber from './CanvasPerio/PerioToothNumber';
import IndicesDialog from './IndicesDialog';
import VisitInfoDialog from './VisitInfoDialog';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from './Chart.redux';
import { resetSVGSubscriptions } from 'svgHandlers/subscribe';
import TextDialog from './TextDialog';
import CodeDefinitions from './CodeDefinitions';
import TextNotes from './TextNotes';
import SnapshotDialog from './SnapshotDialog';
import PenpalContextBuccal from './PenpalContextBuccal';
import PenpalContextOcclusal from './PenpalContextOcclusal';
import PenPal from 'lib/PenPal';
import useNavigationWarning from 'hooks/useNavigationWarning';
import SetTitle from 'actionComponents/SetTitle';
import dayjs from 'dayjs';
import CodeHandler from 'pages/Chart/CodeHandler/CodeHandler';
import { SaveOutOfSyncWarningModal } from 'components/SaveOutOfSyncWarningModal';

const Chart = () => {
    const theme = useTheme();
    const classes = chartSx(theme),
        [buccalSize, setBuccalSize] = useState(9),
        probingActive = useSelector((state) => state.chart.toolbar.selectedTool === 'probe'),
        patientName = useSelector((state) => state.chart.patient_name),
        clientName = useSelector((state) => state.chart.client_name),
        visitDate = useSelector((state) => state.chart.visit_date),
        dispatch = useDispatch(),
        { chart_id } = useParams(),
        [penPals] = useState({
            occlusal: new PenPal(),
            buccal: new PenPal(),
            perio: new PenPal(),
        }),
        [Prompt, setDirty, setClean] = useNavigationWarning();

    const showOutOfSyncModal = useSelector((state) => state.chart.showOutOfSyncModal);
    const outOfSyncMessage = useSelector((state) => state.chart.outOfSyncMessage);
    const saving = useSelector((state) => state.chart.saving);
    const loading = useSelector((state) => state.chart.loading);

    const handleCloseOutOfSyncModal = useCallback(() => {
        dispatch(actions.setState({ showOutOfSyncModal: false, outOfSyncMessage: '' }));
    }, [dispatch]);

    const handleForceSave = useCallback(() => {
        dispatch(actions.saveChart(false, true));
    }, [dispatch]);

    const handleRefreshData = useCallback(() => {
        dispatch(actions.getChart(chart_id));
    }, [dispatch, chart_id]);

    useEffect(() => {
        dispatch(actions.getChart(chart_id));
    }, [dispatch, chart_id]);

    // Set buccal canvas size based on whether or not the probing section is active
    useEffect(() => {
        setBuccalSize(probingActive ? 6 : 9);
    }, [setBuccalSize, probingActive]);

    useEffect(() => {
        setDirty();

        return () => {
            setClean();
            resetSVGSubscriptions();
            dispatch(actions.resetAll());
        };
    }, []);

    return (
        <PenpalContextBuccal.Provider value={penPals.buccal}>
            <PenpalContextOcclusal.Provider value={penPals.occlusal}>
                <div>
                    <SetTitle
                        title={
                            patientName
                                ? `Chart - ${patientName} (${clientName}, ${dayjs(visitDate).format('MM/DD/YYYY')})`
                                : 'Chart'
                        }
                    />

                    <div>
                        <ChartingMenu />
                    </div>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 3 }}>
                            <Paper elevation={2} sx={classes.occlusalContainer}>
                                <Box sx={classes.canvasWrapper}>
                                    <CanvasOcclusal penPal={penPals.occlusal} />
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid size={{ xs: buccalSize }}>
                            <Paper elevation={2} sx={classes.buccalContainer}>
                                <Box sx={{ ...classes.canvasWrapper, ...classes.buccalCanvasWrapper }}>
                                    <CanvasBuccal penPal={penPals.buccal} />
                                </Box>
                            </Paper>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <TextNotes />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <CodeDefinitions title="Diagnostic Code Definitions" symbolType="dx" />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <CodeDefinitions title="Treatment Code Definitions" symbolType="tx" />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <CodeDefinitions title="Other Code Definitions" symbolType="other" />
                                </Grid>
                            </Grid>
                        </Grid>

                        {probingActive && (
                            <Grid size={{ xs: 3 }}>
                                <Paper elevation={2} sx={classes.perioContainer}>
                                    <div>
                                        <PerioToothNumber />
                                    </div>
                                    <Box sx={classes.canvasWrapper}>
                                        <CanvasPerio penPal={penPals.perio} />
                                    </Box>
                                </Paper>
                                <Paper elevation={2} sx={classes.probingPad}>
                                    <PerioNumpad />
                                </Paper>
                            </Grid>
                        )}
                    </Grid>

                    <IndicesDialog />
                    <VisitInfoDialog />
                    {/*<SymbolHandler />*/}
                    <CodeHandler />

                    <TextDialog />
                    <SnapshotDialog />
                </div>
                {Prompt}
                <SaveOutOfSyncWarningModal
                    open={showOutOfSyncModal}
                    message={outOfSyncMessage}
                    onClose={handleCloseOutOfSyncModal}
                    onForceSave={handleForceSave}
                    onRefreshData={handleRefreshData}
                    loading={saving || loading}
                    title="Chart out of sync"
                />
            </PenpalContextOcclusal.Provider>
        </PenpalContextBuccal.Provider>
    );
};

export default Chart;
